import FilePresentIcon from '@mui/icons-material/FilePresent';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';
import {
	Control,
	Controller,
	ControllerProps,
	FieldError,
	FieldValues,
	Path,
	UseFormRegister,
} from 'react-hook-form';

export type TextFieldElementProps<T extends FieldValues = FieldValues> = Omit<
	TextFieldProps,
	'name'
> & {
	accept?: string;
	validation?: ControllerProps['rules'];
	name: Path<T>;
	control: Control<T>;
	register: UseFormRegister<T>;
	parseError?: (error: FieldError) => string;
};

export default function FileUploadField<
	TFieldValues extends FieldValues = FieldValues,
>({
	validation = {},
	parseError,
	type,
	required,
	name,
	control,
	disabled,
	label,
	register,
	accept,
	sx,
	...rest
}: TextFieldElementProps<TFieldValues>): JSX.Element {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState: { error } }) => (
				<FormControl error={!!error} variant='standard' sx={sx}>
					<Box width='100%'>
						<label htmlFor={name}>
							<Button
								component='span'
								disabled={disabled}
								fullWidth
								variant='outlined'
								color='secondary'
								startIcon={<FileUploadIcon />}
							>
								{label}
							</Button>
						</label>
						<input
							{...register(name)}
							disabled={disabled}
							type='file'
							accept={accept}
							id={name}
							style={{ display: 'none' }}
						/>
					</Box>
					{!!error && error.message && (
						<FormHelperText sx={{ ml: '14px', mr: '14px' }}>
							{error.message}
						</FormHelperText>
					)}
					{field.value && field?.value[0]?.name && (
						<Typography
							variant='caption'
							sx={{
								display: 'flex',
								alignItems: 'center',
								mt: 2,
							}}
						>
							<FilePresentIcon />
							<Box
								sx={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{field.value[0].name}
							</Box>
						</Typography>
					)}
				</FormControl>
			)}
		/>
	);
}
